import React from "react";
import { API_ENDPOINTS, getAuthHeaders } from "../config/api";
import { cacheGet, cacheSet, emitCacheUpdated } from "../utils/cache";
import { getWeekMonday, formatLabelDDMM, buildWeekMondays, addDays } from "../utils/dateTime";
import { onBroadcastCacheUpdate } from "../utils/broadcast";
import { mergeAttendance } from "../utils/attendanceMerge";

// Les fonctions getWeekMonday, formatLabelDDMM, buildWeekMondays sont maintenant dans utils/dateTime.js

/**
 * Calcule un maxY "agréable" pour l'échelle
 * @param {number} rawMax - Valeur maximale brute
 * @returns {number} Valeur arrondie pour l'échelle
 */
function niceMaxY(rawMax) {
  if (rawMax === 0) return 1; // Éviter échelle dégénérée
  if (rawMax <= 5) return 5;
  if (rawMax <= 10) return 10;
  if (rawMax <= 20) return 20;
  if (rawMax <= 50) return 50;
  // Arrondir au multiple de 10 supérieur
  return Math.ceil(rawMax / 10) * 10;
}

/**
 * Vérifie si on doit charger depuis l'API (UNIQUEMENT si période manquante)
 * Le cache n'expire JAMAIS automatiquement (mise à jour au login ou modification manuelle)
 * @param {Object} lastFetch - Dernière info de fetch
 * @param {string} from - Date de début demandée
 * @param {string} to - Date de fin demandée
 * @returns {boolean} True si on doit fetcher
 */
function shouldFetchFromAPI(lastFetch, from, to) {
  if (!lastFetch || !lastFetch.from || !lastFetch.to) {
    return true; // Pas de cache du tout
  }
  
  // 🔥 CHANGEMENT: Plus de vérification d'âge (5 min)
  // Le cache est persistant jusqu'à mise à jour explicite (login ou modification)
  
  // Vérifier UNIQUEMENT si la période demandée est hors du cache
  const requestedFrom = new Date(from);
  const requestedTo = new Date(to);
  const cachedFrom = new Date(lastFetch.from);
  const cachedTo = new Date(lastFetch.to);
  
  if (requestedFrom < cachedFrom || requestedTo > cachedTo) {
    console.log('⚠️ Période hors cache, chargement progressif');
    return true;
  }
  
  console.log('✅ Période en cache, utilisation données locales');
  return false;
}

/**
 * Traite les données d'attendance et génère les séries pour le graphique
 * @param {Array} operators - Liste des opérateurs
 * @param {Array} attendance - Données d'attendance brutes
 * @param {string} from - Date de début
 * @param {string} to - Date de fin
 * @returns {Object} Données traitées (weeks, operators, series, maxY, rawData)
 */
function processAttendanceData(operators, attendance, from, to) {
  // Normaliser les opérateurs
  const allOperators = operators.map(op => ({
    id: Number(op.id),
    name: op.name || `Opérateur #${op.id}`
  })).sort((a, b) => a.name.localeCompare(b.name));
  
  // Créer une map pour faciliter la recherche d'opérateurs par ID
  const operatorMap = new Map();
  allOperators.forEach(op => operatorMap.set(op.id, op.name));
  
  // Générer tous les lundis de la période
  const weekMondays = buildWeekMondays(from, to);
  const weekLabels = weekMondays.map(formatLabelDDMM);
  
  // 🔥 FILTRER les données par SEMAINES COMPLÈTES (pas juste le mois)
  // Inclure les données de la première et dernière semaine même si hors du mois
  const firstWeekStart = weekMondays[0] || from;
  const lastWeekEnd = weekMondays.length > 0 
    ? addDays(weekMondays[weekMondays.length - 1], 6) // Dimanche de la dernière semaine
    : to;
  
  const filteredAttendance = attendance.filter(row => {
    if (!row.local_date) return false;
    const rowDate = row.local_date.split('T')[0]; // YYYY-MM-DD
    return rowDate >= firstWeekStart && rowDate <= lastWeekEnd;
  });
  
  console.log(`📊 [processAttendanceData] Période mois: ${from} → ${to}`);
  console.log(`📊 [processAttendanceData] Période semaines: ${firstWeekStart} → ${lastWeekEnd}`);
  console.log(`📊 [processAttendanceData] Données brutes: ${attendance.length} | Filtrées: ${filteredAttendance.length}`);
  
  // Initialiser toutes les semaines avec tous les opérateurs à 0
  const mapWeeks = new Map();
  weekMondays.forEach(monday => {
    const opMap = new Map();
    allOperators.forEach(op => opMap.set(op.id, 0));
    mapWeeks.set(monday, opMap);
  });
  
  // Parcourir UNIQUEMENT les lignes FILTRÉES et agréger les minutes
  // 🔥 IMPORTANT: Ne comptabiliser que les jours du mois strict (from → to)
  filteredAttendance.forEach(row => {
    if (!row.local_date) return;
    
    // 🔥 FILTRAGE STRICT: Vérifier que le jour est bien dans le mois demandé
    const rowDate = row.local_date.split('T')[0];
    if (rowDate < from || rowDate > to) {
      // Ce jour est dans une semaine chevauchante mais hors du mois → SKIP
      return;
    }
    
    // Détecter l'ID opérateur
    const opId = row.operator?.id || row.operator_id;
    if (!opId) return;
    
    // Calculer les minutes de la journée
    const workMins = Number(row.work_minutes);
    const workHrs = Number(row.work_hours);
    
    let dayMinutes = 0;
    if (!isNaN(workMins)) {
      dayMinutes = workMins;
    } else if (!isNaN(workHrs)) {
      dayMinutes = Math.round(workHrs * 60);
    }
    
    // Trouver le lundi de cette journée
    const monday = getWeekMonday(row.local_date);
    
    // Ajouter au total hebdomadaire
    if (mapWeeks.has(monday)) {
      const opMap = mapWeeks.get(monday);
      const current = opMap.get(Number(opId)) || 0;
      opMap.set(Number(opId), current + dayMinutes);
    }
  });
  
  // Conversion en heures pour le chart
  const allSeries = {};
  let rawMax = 0;
  
  allOperators.forEach(op => {
    allSeries[op.id] = weekMondays.map(monday => {
      const minutes = mapWeeks.get(monday)?.get(op.id) || 0;
      const hours = minutes / 60;
      rawMax = Math.max(rawMax, hours);
      return hours;
    });
  });
  
  const maxY = niceMaxY(rawMax);
  
  // Préparer les données brutes pour le tableau (triées par date décroissante)
  // 🔥 TABLEAU: Filtrage strict par MOIS uniquement (pas les débordements de semaine)
  const tableOnlyAttendance = filteredAttendance.filter(row => {
    const rowDate = row.local_date.split('T')[0];
    return rowDate >= from && rowDate <= to;
  });
  
  const rawDataForTable = tableOnlyAttendance
    .map(row => {
      let workMins = 0;
      if (row.work_minutes != null) {
        workMins = Number(row.work_minutes);
      } else if (row.work_hours != null) {
        workMins = Math.round(Number(row.work_hours) * 60);
      }
      
      let breakMins = 0;
      if (row.break_minutes != null) {
        breakMins = Number(row.break_minutes);
      } else if (row.break_hours != null) {
        breakMins = Math.round(Number(row.break_hours) * 60);
      }
      
      return {
        id: row.id,
        local_date: row.local_date,
        operator_id: row.operator_id || row.operator?.id,
        operator_name: operatorMap.get(Number(row.operator_id || row.operator?.id)) || 'Inconnu',
        check_in: row.check_in,
        check_out: row.check_out,
        break_start: row.break_start,
        break_end: row.break_end,
        break_minutes: breakMins,
        work_minutes: workMins
      };
    })
    .sort((a, b) => new Date(b.local_date) - new Date(a.local_date));
  
  return {
    weeks: weekLabels,
    operators: allOperators,
    series: allSeries,
    maxY,
    rawData: rawDataForTable
  };
}

/**
 * Hook pour récupérer et agréger les données d'attendance par semaine et opérateur
 * @param {{from: string, to: string}} range - Période (YYYY-MM-DD)
 * @param {number[]|null} selectedOperatorIds - IDs des opérateurs à inclure (null/[] = tous)
 * @returns {{weeks: string[], operators: {id:number, name:string}[], series: Record<number, number[]>, maxY: number, loading: boolean, error: Error|null, refresh: Function}}
 */
export function useAttendanceWeeks({ from, to }, selectedOperatorIds = null) {
  const [state, setState] = React.useState(() => {
    // 🔹 Charger immédiatement depuis le cache pour affichage instantané
    const cachedOperators = cacheGet('activity/operators', []);
    const cachedAttendance = cacheGet('activity/attendance', []);
    
    // Si on a des données en cache, les afficher immédiatement
    if (cachedOperators.length > 0 || cachedAttendance.length > 0) {
      console.log('⚡ [useAttendanceWeeks] Chargement instantané depuis le cache');
      const initialData = processAttendanceData(cachedOperators, cachedAttendance, from, to);
      return {
        ...initialData,
        loading: false,
        error: null,
      };
    }
    
    return {
      weeks: [],
      operators: [],
      series: {},
      maxY: 0,
      rawData: [],
      loading: true,
      error: null,
    };
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  
  // 🔥 FIX: Utiliser ref pour from/to pour éviter closure stale
  const rangeRef = React.useRef({ from, to });
  rangeRef.current = { from, to };

  const fetcher = React.useCallback(async (silent = false) => {
    // 🔥 FIX: Utiliser rangeRef pour from/to
    const { from, to } = rangeRef.current;
    
    if (!silent) {
      setState((s) => ({ ...s, loading: true, error: null }));
    } else {
      setIsRefreshing(true);
    }
    
    try {
      // Récupérer l'ID de l'utilisateur connecté
      const userId = localStorage.getItem('userId') || localStorage.getItem('uid') || localStorage.getItem('user_id') || "";
      
      // Vérifier si on a besoin de charger depuis l'API
      const lastFetch = cacheGet('activity/lastFetch', null);
      const needsFetch = shouldFetchFromAPI(lastFetch, from, to);
      
      // 🔥 Si on doit charger et qu'on n'a pas les données en cache, 
      // réinitialiser les colonnes à 0 d'abord
      if (needsFetch && !silent) {
        const cachedOperators = cacheGet('activity/operators', []);
        if (cachedOperators.length > 0) {
          // Mettre toutes les colonnes à 0 pendant le chargement
          const emptyData = processAttendanceData(cachedOperators, [], from, to);
          setState({
            ...emptyData,
            loading: true,
            error: null,
          });
        }
      }
      
      if (!needsFetch && !silent) {
        console.log('✅ [useAttendanceWeeks] Données déjà en cache, pas de fetch nécessaire');
        const cachedOperators = cacheGet('activity/operators', []);
        const cachedAttendance = cacheGet('activity/attendance', []);
        const processedData = processAttendanceData(cachedOperators, cachedAttendance, from, to);
        setState({
          ...processedData,
          loading: false,
          error: null,
        });
        setIsRefreshing(false);
        return;
      }
      
      // 1. Récupérer la liste complète des opérateurs
      const operatorsUrl = API_ENDPOINTS.OPERATORS(userId);
      console.log('👥 [useAttendanceWeeks] Fetching operators:', operatorsUrl);
      
      const operatorsRes = await fetch(operatorsUrl, { headers: getAuthHeaders() });
      if (!operatorsRes.ok) {
        const text = await operatorsRes.text();
        throw new Error(`Operators API ${operatorsRes.status}: ${text}`);
      }
      
      const allOperatorsFromAPI = await operatorsRes.json();
      console.log('✅ [useAttendanceWeeks] Received', allOperatorsFromAPI.length, 'operators from API');
      
      // 2. Récupérer les données de présence
      const attendanceUrl = API_ENDPOINTS.ATTENDANCE_DAYS(from, to, userId);
      console.log('📊 [useAttendanceWeeks] Fetching attendance:', attendanceUrl);
      
      const res = await fetch(attendanceUrl, { headers: getAuthHeaders() });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Attendance API ${res.status}: ${text}`);
      }
      
      const rows = await res.json();
      console.log('✅ [useAttendanceWeeks] Received', rows.length, 'attendance rows');
      
      // Normaliser les opérateurs
      const normalizedOperators = allOperatorsFromAPI.map(op => ({
        id: Number(op.id),
        name: op.name || `${op.first_name || ''} ${op.last_name || ''}`.trim() || `Opérateur #${op.id}`
      }));
      
      // Mettre à jour le cache avec merge (ne pas écraser)
      cacheSet('activity/operators', normalizedOperators);
      
      // Merge attendance au lieu d'écraser
      const existing = cacheGet('activity/attendance', []);
      const merged = mergeAttendance(existing, rows);
      cacheSet('activity/attendance', merged);
      
      cacheSet('activity/lastFetch', { from, to, at: Date.now() });
      emitCacheUpdated('activity/attendance');
      
      // Traiter les données avec la fonction helper
      const processedData = processAttendanceData(normalizedOperators, rows, from, to);
      
      setState({
        ...processedData,
        loading: false,
        error: null,
      });
      
      setIsRefreshing(false);
      
    } catch (error) {
      console.error('❌ [useAttendanceWeeks] Error:', error);
      setState(s => ({
        ...s,
        loading: false,
        error,
      }));
      setIsRefreshing(false);
    }
  }, []); // 🔥 FIX: Plus de dépendances [from, to] (on utilise rangeRef)

  // Écouter les événements de mise à jour du cache (DOM + BroadcastChannel)
  const isMountedCacheRef = React.useRef(true);
  
  React.useEffect(() => {
    isMountedCacheRef.current = true;
    const rafIdRef = { current: 0 };
    const pendingRef = { current: false };
    
    function scheduleRead() {
      if (pendingRef.current) return;
      pendingRef.current = true;
      rafIdRef.current = window.requestAnimationFrame(() => {
        pendingRef.current = false;
        
        // 🔥 FIX: Vérifier que le composant est encore monté avant setState
        if (!isMountedCacheRef.current) {
          return;
        }
        
        // 🔥 FIX: Utiliser rangeRef.current pour avoir les valeurs les plus récentes
        const { from: currentFrom, to: currentTo } = rangeRef.current;
        const cachedOperators = cacheGet('activity/operators', []);
        const cachedAttendance = cacheGet('activity/attendance', []);
        const processedData = processAttendanceData(cachedOperators, cachedAttendance, currentFrom, currentTo);
        
        // Double vérification
        if (isMountedCacheRef.current) {
          setState(s => ({
            ...processedData,
            loading: s.loading,
            error: s.error,
          }));
        }
      });
    }

    const onUpd = (e) => {
      const topic = e?.detail?.topic;
      if (!topic) return;
      // Écouter le topic exact + fallback sur prefix + bootstrap
      if (topic === 'activity/attendance' || topic === 'activity' || topic === 'bootstrap') {
        scheduleRead();
      }
    };
    
    // Écouter événement DOM (onglet actuel)
    window.addEventListener('cc:cache-updated', onUpd);
    
    // Écouter BroadcastChannel (autres onglets)
    const cleanupBroadcast = onBroadcastCacheUpdate((topic) => {
      // Écouter le topic exact + fallback sur prefix + bootstrap
      if (topic === 'activity/attendance' || topic === 'activity' || topic === 'bootstrap') {
        scheduleRead();
      }
    });
    
    return () => {
      isMountedCacheRef.current = false;
      window.removeEventListener('cc:cache-updated', onUpd);
      cleanupBroadcast();
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      pendingRef.current = false;
    };
  }, []); // 🔥 FIX: Plus de dépendances from/to (on utilise rangeRef)

  // 🔥 FIX: Appeler fetcher quand from/to changent
  React.useEffect(() => {
    fetcher(false);
  }, [from, to, fetcher]);

  // ===== FILTRAGE LOCAL PAR OPÉRATEUR SÉLECTIONNÉ =====
  const filteredData = React.useMemo(() => {
    // Si null ou vide, tous sont affichés
    if (!selectedOperatorIds || selectedOperatorIds.length === 0) {
      return state;
    }
    
    const selectedSet = new Set(selectedOperatorIds);
    const filteredOperators = state.operators.filter(op => selectedSet.has(op.id));
    const filteredSeries = {};
    let rawMax = 0;
    
    filteredOperators.forEach(op => {
      filteredSeries[op.id] = state.series[op.id] || [];
      const opMax = Math.max(...filteredSeries[op.id]);
      rawMax = Math.max(rawMax, opMax);
    });
    
    const maxY = niceMaxY(rawMax);
    
    // Filtrer aussi les données brutes
    const filteredRawData = state.rawData.filter(row => 
      selectedSet.has(Number(row.operator_id))
    );
    
    return {
      ...state,
      operators: filteredOperators,
      series: filteredSeries,
      rawData: filteredRawData,
      maxY,
    };
  }, [state, selectedOperatorIds]);

  return {
    ...filteredData,
    isRefreshing,
    refresh: () => fetcher(true),
  };
}